import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  Section,
  Container,
  Typography,
  Grid,
  Anchor,
  Form,
  Button,
  Card,
} from 'components/base';
import { ProductCard, WIDTH_LADDERS } from 'components/rich';
import useClaimedCoupon from 'ecommerce/context/claimedCoupon';
import { couponSale } from 'ecommerce/lib/coupon';
import httpRequest from 'utils/httpRequest';
import { isEmailValid } from 'utils/validator';

import SETTINGS from '../../settings';
import CatalogCategoryCard from './catalogCategoryCard';
import { ProductDetailLink, CategoryPageLink } from '../pageLinks';

// Bootstrap 4 defaults baked into components/base/grid.scss: xl container caps at 1140px,
// container + column gutters are 15px a side.
const GRID_COLS = ['xs', 'sm', 'md', 'lg', 'xl'];
const XL_CONTENT_WIDTH = 1140 - 30;

// `sizes` for an image filling a Grid.Col: fixed px inside the capped xl container, viewport
// fractions below it. Between breakpoints the fraction slightly overestimates the capped
// container, which only ever makes the browser pick a larger srcset candidate.
const colImageSizes = colProps => {
  const fractions = [];
  let current = 12;
  for (const key of GRID_COLS) {
    if (colProps[key] != null) current = colProps[key];
    fractions.push(current / 12);
  }
  const [xs, sm, md, lg, xl] = fractions;

  const entries = [
    ['(min-width: 1200px)', `${Math.round(XL_CONTENT_WIDTH * xl - 30)}px`],
    ['(min-width: 992px)', `${Math.round(lg * 100)}vw`],
    ['(min-width: 768px)', `${Math.round(md * 100)}vw`],
    ['(min-width: 576px)', `${Math.round(sm * 100)}vw`],
    ['', `${Math.round(xs * 100)}vw`],
  ];

  // First matching media condition wins, so collapse runs of equal values down to the entry
  // with the smallest breakpoint.
  return entries
    .filter(([, value], i) => i === entries.length - 1 || value !== entries[i + 1][1])
    .map(([query, value]) => `${query} ${value}`.trim())
    .join(', ');
};

export const ProductsSection = props => {
  const claimedCoupon = useClaimedCoupon();

  const colProps = {
    xs: props.xs,
    sm: props.sm,
    md: props.md,
    lg: props.lg,
    xl: props.xl,
  };
  const imageSizes = colImageSizes(colProps);

  const cardClassName = classNames({
    'card-equalize': true,
    'card-mobile-compact': colProps.xs === 6,
    'card-img-square': true,
  });

  const subCategories = props.subCategories.map(category => (
    <Grid.Col key={category.id} {...colProps}>
      <CategoryPageLink category={category}>
        <CatalogCategoryCard
          asLink
          category={category}
          previewSummary={claimedCoupon.previewSummary}
          className={cardClassName}
          widths={WIDTH_LADDERS.categories}
          sizes={imageSizes}
        />
      </CategoryPageLink>
    </Grid.Col>
  ));

  const products = props.products.map(product => (
    <Grid.Col key={product.id} {...colProps}>
      <ProductDetailLink productId={product.id}>
        <ProductCard
          asLink
          product={product}
          sale={couponSale(claimedCoupon.previewSummary, product)}
          className={cardClassName}
          widths={WIDTH_LADDERS.products}
          sizes={imageSizes}
        />
      </ProductDetailLink>
    </Grid.Col>
  ));

  return (
    <Section>
      <Container>
        {props.title && <Typography.H2 className="section-title mb-2">{props.title}</Typography.H2>}
        <Grid.Row>
          {props.productsFirst ? (
            <>
              {products}
              {subCategories}
            </>
          ) : (
            <>
              {subCategories}
              {products}
            </>
          )}
        </Grid.Row>
      </Container>
    </Section>
  );
};

ProductsSection.propTypes = {
  title: PropTypes.string,
  subCategories: PropTypes.array,
  products: PropTypes.array,
  productsFirst: PropTypes.bool,
  xs: PropTypes.number,
  sm: PropTypes.number,
  md: PropTypes.number,
  lg: PropTypes.number,
  xl: PropTypes.number,
};

ProductsSection.defaultProps = {
  subCategories: [],
  products: [],
  productsFirst: false,
};

// The device mockup is cut off at the bottom in the source asset, so the section drops its
// bottom padding and lets the phone bleed off the edge (see .section-app-promo).
const APP_DEVICE_SIZES = '(min-width: 1200px) 540px, (min-width: 768px) 50vw, 100vw';

export const AppPromoSection = ({ eyebrow, title, body, href, onCtaClick }) => (
  <Section className="section-app-promo">
    <Container>
      <Grid.Row className="align-items-center">
        <Grid.Col xs={12} md={6} lg={5} className="offset-lg-1 col-app-copy">
          <span className="small text-success font-weight-bold text-uppercase">{eyebrow}</span>
          <Typography.H2 className="mt-1">{title}</Typography.H2>
          <Typography.P className="text-light mb-4">{body}</Typography.P>
          <Anchor href={href} className="link-app-store" targetBlank onClick={onCtaClick}>
            <img
              src="/static/img/app-store-badge.svg"
              alt="Download Parabo Press on the App Store"
              width="120"
              height="40"
              loading="lazy"
            />
          </Anchor>
        </Grid.Col>
        <Grid.Col xs={12} md={6} className="col-app-device">
          <img
            src="/static/img/app-device-720.webp"
            srcSet="/static/img/app-device-480.webp 480w, /static/img/app-device-720.webp 720w, /static/img/app-device-1080.webp 1080w"
            sizes={APP_DEVICE_SIZES}
            width="1080"
            height="1049"
            alt="The Parabo Press iPhone app showing featured Square Prints and Photo Books"
            loading="lazy"
          />
        </Grid.Col>
      </Grid.Row>
    </Container>
  </Section>
);

AppPromoSection.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.string,
  body: PropTypes.string,
  href: PropTypes.string,
  onCtaClick: PropTypes.func,
};

AppPromoSection.defaultProps = {
  eyebrow: 'Now on your iPhone',
  title: 'Print straight from your camera roll',
  body: 'Your favorite moments already live on your phone. The Parabo Press app turns them into prints, books and cards in a few taps, then ships them to your door.',
  href: SETTINGS.APP_STORE_URL,
  onCtaClick: undefined,
};

export const BlogSection = props => (
  <Section>
    <Container>
      <Typography.H2 className="section-title mb-2">FROM OUR BLOG</Typography.H2>
      <Grid.Row className="blog-grid">
        {props.data.slice(0, 3).map(article => (
          <Grid.Col key={article.uid} xs={12} md={4} className="blog-grid-col">
            <Card asLink href={article.link} targetBlank>
              {article.image && (
                <div className="card-img-top">
                  <img src={article.image} alt={article.title} loading="lazy" />
                </div>
              )}
              <Card.Body>
                <Typography.H4>
                  <span>{article.title}</span>
                </Typography.H4>
                {article.description && <div className="card-text">{article.description}</div>}
                <span className="link-arrow">READ MORE</span>
              </Card.Body>
            </Card>
          </Grid.Col>
        ))}
      </Grid.Row>
      <div className="text-center">
        <Button type="outline" asLink href="/blog/">
          View All Articles
        </Button>
      </div>
    </Container>
  </Section>
);

BlogSection.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      uid: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
      link: PropTypes.string,
      image: PropTypes.string,
      publishedAt: PropTypes.string,
    }),
  ),
};

export const NewsletterSection = props => {
  return (
    <Section type="green" className={props.className}>
      <Container className="text-center mb-5">
        <Typography.H2 className="section-title">Subscribe to our newsletter</Typography.H2>
        <Typography.P className="text-light mt-2 mb-5">
          Be the first to find out when we have sales, add new products, or send along home decor
          inspo.
        </Typography.P>

        <Button asLink href={SETTINGS.NEWSLETTER_URL} targetBlank type="success">
          Subscribe
        </Button>
      </Container>
    </Section>
  );
};

NewsletterSection.propTypes = {
  className: PropTypes.string,
};
